import { F, STATE_STRIDE } from './constants.ts';

/**
 * Terrain / landscape evolution kernels — shared numerical definitions.
 *
 * These are the *reference* versions of every physical law used by the
 * simulator. `gpu/wgsl/passes.wgsl` is a line-by-line WGSL translation of this
 * file; when a constant or a formula changes it must change in both places.
 * Keeping the CPU implementation alive is not a compromise: it is what lets the
 * physics be validated offline (see `test/`), and it doubles as the fallback
 * backend for machines without WebGPU.
 *
 * Physical model summary
 * ----------------------
 *  water flow      : shallow-water "pipe" approximation (Mei et al. 2007 style
 *                    virtual pipes, with damping) instead of a full 2D solve
 *  sediment        : capacity-limited advection. Suspended load is transported
 *                    by the depth-averaged velocity field; the water either
 *                    erodes towards capacity or settles the excess.
 *  thermal erosion : slope-limited mass wasting using the angle of repose.
 *  wind erosion    : shear-driven entrainment of *loose* material only, with a
 *                    terrain-shadow shelter metric controlling deposition.
 */



export const GRAVITY = 9.81;

/** Above this speed the depth-averaged velocity is clipped [m/s]. */
export const MAX_WATER_SPEED = 25;

/**
 * Maximum suspended-sediment concentration, as a volume fraction of the water
 * column [-]. Above roughly this value a water-sediment mixture stops behaving
 * like water (it is a debris flow), and numerically it is the difference
 * between a stable model and a runaway one: a millimetre-deep film cannot hold
 * metres of sediment, so capping the concentration keeps thin water thin in
 * effect as well as in appearance.
 */
export const MAX_SEDIMENT_CONCENTRATION = 0.5;

/** Wind speed below which no loose material moves [m/s]. */
export const WIND_THRESHOLD = 4.0;

/** Parameter fractions are calibrated at the historical two-second tick. */
export const REFERENCE_DT = 2;

/* -------------------------------------------------------------------------- */
/* Small helpers                                                              */
/* -------------------------------------------------------------------------- */

export function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}

/**
 * Scale a reference-step fraction to an arbitrary duration/subdivision.
 * Repeated calls compose exactly: two half steps equal one full step.
 */
export function timeScaledFraction(fraction: number, dt: number, subdivisions = 1): number {
  const f = clamp(fraction, 0, 1);
  if (f <= 0 || dt <= 0) return 0;
  if (f >= 1) return 1;
  return 1 - Math.pow(1 - f, dt / (REFERENCE_DT * Math.max(1, subdivisions)));
}

/** Keep water sub-step duration stable when the outer simulation dt changes. */
export function scaledSubsteps(referenceSubsteps: number, dt: number): number {
  return clamp(Math.round(referenceSubsteps * dt / REFERENCE_DT), 1, 32);
}

/* -------------------------------------------------------------------------- */
/* Water flow                                                                 */
/* -------------------------------------------------------------------------- */

/**
 * Maximum free-surface conductivity that keeps the transport stable.
 *
 * The velocity diagnosis plus the face-flux transport is an explicit diffusion
 * of the free surface with coefficient `K` [m^2/s], so classical stability for
 * a 2-D explicit scheme requires
 *
 *     K * dt / dx^2  <=  0.25
 *
 * Deriving the bound from the grid and timestep (rather than trusting a
 * parameter range) is what keeps a level lake an equilibrium state: exceed it
 * and the surface oscillates with growing amplitude instead of levelling.
 */
export function maxStableConductivity(cellSize: number, dt: number): number {
  return (0.25 * cellSize * cellSize) / Math.max(dt, 1e-6);
}

/**
 * Free-surface conductivity actually used by the solver [m^2/s].
 *
 * `flowRate` is the UI's *Flow response*, in units of the grid's maximum stable
 * conductivity so the slider stays meaningful at every resolution. `damping`
 * provides additional semi-implicit resistance.
 */
export function flowConductivity(flowRate: number, damping: number, dt: number, cellSize: number): number {
  const limit = maxStableConductivity(cellSize, dt);
  const raw = (flowRate * limit) / (1 + damping * dt);
  return Math.max(0, Math.min(raw, limit));
}

/**
 * Depth-averaged flow velocity from the drop in free surface across one face.
 *
 *     u = -K * (surfaceHere - surfaceThere) / dx
 *
 * This is the **diffusive-wave (Darcy) reduction** of the shallow-water
 * momentum equation: water accelerates down the free-surface slope and is
 * resisted by bed friction, with both folded into the conductivity `K`.
 *
 * A Manning law (`u ~ d^(2/3) sqrt(S) / n`) gives more realistic river
 * velocities, but at these cell sizes and timesteps its stability limit sits
 * far outside the range the grid allows, so the surface oscillates instead of
 * levelling. The diffusive reduction is contractive within the bound above and
 * still produces what landscape evolution needs: flow follows the surface
 * slope, converges into hollows, and ponds in closed basins.
 *
 * @param drop   free-surface drop from this cell to the receiving neighbour [m]
 * @param dx     cell size [m]
 * @param conductivity K [m^2/s]
 * @returns flow speed towards that neighbour [m/s], never negative
 */
export function faceVelocity(drop: number, dx: number, conductivity: number): number {
  if (!(drop > 0)) return 0;
  const u = (conductivity * drop) / dx;
  return Number.isFinite(u) ? Math.min(u, MAX_WATER_SPEED) : 0;
}

/**
 * Water crossing one cell boundary in one sub-step, as a depth [m].
 *
 * `velocity` is the boundary-normal component *of the donor cell*, so this is a
 * pure function of the donor state. Note that for a two-cell boundary both
 * donors evaluate the identical value: `f(depthA, +uA)` equals
 * `f(depthB, -uB)` whenever the velocity is the gradient of a single surface
 * field, which is what makes the advection telescoping and exactly
 * conservative.
 */
export function boundaryFlux(
  donorDepth: number,
  velocity: number,
  dtSub: number,
  cellSize: number,
): number {
  if (velocity === 0 || donorDepth <= 0) return 0;
  const cfl = (Math.abs(velocity) * dtSub) / cellSize;
  return Math.min(cfl * donorDepth, donorDepth);
}

/**
 * Signed water flux on the boundary between cell `a` (upwind side, -normal) and
 * cell `b` (downwind side, +normal), positive when water flows a -> b in one
 * sub-step [m of depth].
 *
 * Upwind (donor) selection is done once, here: exactly ONE of the two possible
 * donor contributions can be non-zero, because a donor only contributes flux in
 * the direction of its own velocity. Evaluating both sides independently is the
 * classic finite-volume bookkeeping bug — it double-counts the boundary and
 * invents water out of nothing.
 */
export function signedBoundaryFlux(
  depthA: number,
  depthB: number,
  velA: number,
  velB: number,
  dtSub: number,
  cellSize: number,
): number {
  const forward = velA > 0 ? boundaryFlux(depthA, velA, dtSub, cellSize) : 0;
  const backward = velB < 0 ? boundaryFlux(depthB, -velB, dtSub, cellSize) : 0;
  const net = forward - backward;
  return Number.isFinite(net) ? net : 0;
}

/* -------------------------------------------------------------------------- */
/* Hydraulic erosion                                                          */
/* -------------------------------------------------------------------------- */

/**
 * Sediment capacity of the flow, as a **load** [m of sediment] rather than a
 * concentration:
 *
 *     capacity = capacityFactor * slope * speed * waterDepth
 *
 * which is the classic "stream power with a transport stage" reduction: steeper,
 * faster water carries more, and the load scales with how much water there
 * actually is.
 *
 * The depth factor is not cosmetic. A concentration-based capacity
 * (`capacityFactor * slope * speed`, then multiplied by depth inside the
 * relaxation) is *unbounded* as the water column goes to zero: a millimetre-deep
 * film on a steep slope then has a huge capacity, erodes hard, and the resulting
 * sediment concentration feeds back into the next tick. That is an exponential
 * blow-up, and it is what a depth-scaled load removes — a thin film simply
 * cannot carry much material.
 */
export function sedimentCapacityLoad(
  slope: number,
  speed: number,
  waterDepth: number,
  capacityFactor: number,
): number {
  return capacityFactor * slope * speed * waterDepth;
}

/** Modest stream-power boost from multi-flow upstream contributing area. */
export function accumulationBoost(flowAccum: number): number {
  return 1 + 0.12 * Math.log2(Math.max(1, flowAccum));
}

/**
 * Net elevation change from capacity-limited erosion/deposition.
 *
 * `suspendedLoad` and `capacityLoad` are both **loads in metres of sediment**
 * (a depth-equivalent, not a concentration), so the two are directly
 * comparable and the result is a depth change.
 *
 * Erosion detaches material at a rate proportional to the capacity deficit;
 * deposition settles the excess with first-order kinetics. Both are capped by
 * `maxPerTick` so a single bad timestep cannot gouge the terrain.
 *
 * @param erodibility `erosionStrength / hardness` — hard bedrock detaches
 *        slowly, soft regolith quickly. Ignored for deposition, since settling
 *        is a property of the flow, not of the bed.
 * @returns signed terrain change [m]: negative = eroded, positive = deposited
 */
export function capacityRelaxation(
  suspendedLoad: number,
  capacityLoad: number,
  erodibility: number,
  depositionRate: number,
  maxPerTick: number,
): number {
  let delta: number;
  if (capacityLoad > suspendedLoad) {
    // Under capacity: the flow detaches material. `erodibility` acts as the
    // first-order detachment rate, so the load relaxes towards capacity over
    // roughly 1/erodibility ticks — fast enough to incise channels promptly,
    // slow enough that a single tick cannot gouge the terrain.
    delta = -(capacityLoad - suspendedLoad) * erodibility;
  } else {
    // Over capacity: the excess settles out with first-order kinetics.
    delta = (suspendedLoad - capacityLoad) * depositionRate;
  }
  return clamp(delta, -maxPerTick, maxPerTick);
}

/* -------------------------------------------------------------------------- */
/* Thermal (gravity) erosion                                                  */
/* -------------------------------------------------------------------------- */

/**
 * Maximum stable height difference between two adjacent cells before the slope
 * exceeds the angle of repose.
 */
export function talusLimit(talusAngleDeg: number, cellSize: number): number {
  return Math.tan((talusAngleDeg * Math.PI) / 180) * cellSize;
}

/**
 * Material moved from `here` towards `there` by one thermal relaxation step.
 *
 * Jacobi (gather) formulation: every cell computes its own outflow from the
 * heights of the *previous* iteration, so the update is order independent and
 * cannot produce the directional bias / checkerboard artifacts that a
 * Gauss-Seidel in-place sweep gives. `flowRate` is the fraction of the excess
 * slope moved per iteration; total outflow is additionally capped at the excess
 * height itself, which guarantees the higher cell never drops below its
 * neighbour and therefore kills oscillation.
 */
export function thermalOutflow(
  heightHere: number,
  heightThere: number,
  limit: number,
  flowRate: number,
): number {
  const excess = heightHere - heightThere - limit;
  if (excess <= 0) return 0;
  return excess * flowRate;
}

/**
 * Extra stable Jacobi sweeps for cliffs far beyond the angle of repose.
 *
 * Increasing the per-sweep transfer above its eight-neighbour bound revives
 * the overshoot that creates needle peaks. Repeating the bounded sweep instead
 * gives the desired non-linear response without changing its stability proof.
 * Returning a multiplier (rather than an additive count) preserves the meaning
 * of the user's configured iteration count.
 */
export function thermalIterationMultiplier(state: Float32Array, width: number, talus: number): number {
  const scale = Math.max(talus, 1);
  let worst = 0;
  for (let j = 0; j < width; j++) {
    for (let i = 0; i < width; i++) {
      const height = state[(j * width + i) * STATE_STRIDE + F.height];
      if (i + 1 < width) {
        worst = Math.max(worst, Math.abs(height - state[(j * width + i + 1) * STATE_STRIDE + F.height]) / scale);
      }
      if (j + 1 < width) {
        worst = Math.max(worst, Math.abs(height - state[((j + 1) * width + i) * STATE_STRIDE + F.height]) / scale);
      }
      // A diagonal spans sqrt(2) cell widths, so its repose allowance does too.
      const diagonalScale = scale * Math.SQRT2;
      if (i + 1 < width && j + 1 < width) {
        worst = Math.max(
          worst,
          Math.abs(height - state[((j + 1) * width + i + 1) * STATE_STRIDE + F.height]) / diagonalScale,
        );
      }
      if (i > 0 && j + 1 < width) {
        worst = Math.max(
          worst,
          Math.abs(height - state[((j + 1) * width + i - 1) * STATE_STRIDE + F.height]) / diagonalScale,
        );
      }
    }
  }
  return worst >= 32 ? 3 : worst >= 8 ? 2 : 1;
}

/* -------------------------------------------------------------------------- */
/* Wind erosion                                                               */
/* -------------------------------------------------------------------------- */

/**
 * Terrain shelter (wind shadow) at a cell.
 *
 * Only the *height* of the upwind barrier matters — the upwind distance is used
 * for the shadow's extent, not for its depth. A ridge 20 m above the cell casts
 * a wind shadow for roughly `8 * 20 m` downwind (Lee, gravity-wave scaling),
 * capped at half the domain so the lee of a single peak never blankets the map.
 *
 * @param upwindHeights terrain height at 1, 3 and 8 cells upwind
 * @returns 0 (fully exposed) .. 1 (fully sheltered)
 */
export function windShelter(heightHere: number, upwindHeights: Float32Array, cellSize: number): number {
  let shelter = 0;
  for (let k = 0; k < upwindHeights.length; k++) {
    const blocking = upwindHeights[k] - heightHere;
    if (blocking <= 0) continue;
    const distance = (k === 0 ? 1 : k === 1 ? 3 : 8) * cellSize;
    const extent = Math.min(blocking * 8, cellSize * 40);
    shelter = Math.max(shelter, clamp(1 - distance / extent, 0, 1));
  }
  return shelter;
}

/**
 * Loose material entrained by the wind on one cell [m of sediment].
 *
 * Entrainment requires: wind above threshold, a supply of loose material, a
 * surface the wind can actually shear (exposed, not sheltered) and a slope that
 * faces the wind. Uphill-facing (windward) slopes erode, lee slopes do not.
 */
export function windEntrainment(
  loose: number,
  windSpeed: number,
  exposure: number,
  slopeAlongWind: number,
  strength: number,
  dt: number,
): number {
  if (windSpeed <= WIND_THRESHOLD || loose <= 0) return 0;
  const excess = (windSpeed - WIND_THRESHOLD) / WIND_THRESHOLD;
  const windward = clamp(slopeAlongWind, 0, 1); // only upslope-facing sand moves
  const referenceFraction = Math.min(0.5, clamp(strength * 0.06 * excess * exposure * (0.25 + 0.75 * windward), 0, 1));
  return loose * timeScaledFraction(referenceFraction, dt);
}

/**
 * Fraction of the passing load dropped inside a wind shadow.
 * Deposition is strongest where the flow decelerates the most (deep shelter)
 * and grows with the deposition coefficient.
 */
export function windDepositFraction(shelter: number, deposition: number, dt: number): number {
  // Even exposed grains have a small background settling probability. This was
  // historically `max(frac, .08) * dt`; treating .16 as the reference-step
  // fraction gives the same default appearance and composes across time steps.
  const referenceFraction = clamp(Math.max(shelter * shelter * deposition * REFERENCE_DT, 0.08 * REFERENCE_DT), 0, 1);
  return timeScaledFraction(referenceFraction, dt);
}
